import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { cn } from './cn.js';

type ToastTone = 'default' | 'success' | 'error' | 'gold';

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastApi {
  show: (message: string, tone?: ToastTone) => void;
  success: (m: string) => void;
  error: (m: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

let counter = 0;

const toneStyles: Record<ToastTone, string> = {
  default: 'bg-surface-3 text-text border-line',
  success: 'bg-success/15 text-success border-success/30',
  error: 'bg-danger/15 text-danger border-danger/30',
  gold: 'bg-gold/15 text-gold-light border-gold/30',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: ToastTone = 'default') => {
      const id = ++counter;
      setItems((prev) => [...prev, { id, message, tone }]);
      setTimeout(() => remove(id), 3000);
    },
    [remove],
  );

  const api: ToastApi = {
    show,
    success: (m) => show(m, 'success'),
    error: (m) => show(m, 'error'),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      {/* data-screenshot-hide: Toasts gehören nicht auf den Feedback-Screenshot. */}
      <div
        data-screenshot-hide
        className="pointer-events-none fixed top-[calc(env(safe-area-inset-top)+12px)] left-0 right-0 z-[60] flex flex-col items-center gap-2 px-4"
      >
        {items.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto animate-toast-in glass border rounded-2xl px-4 py-3 text-sm font-medium shadow-card max-w-md w-full text-center',
              toneStyles[t.tone],
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) {
    return {
      show: () => {},
      success: () => {},
      error: () => {},
    };
  }
  return v;
}

export function useOptionalToast(): ToastApi | null {
  return useContext(Ctx);
}
